/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the "Elastic License
 * 2.0", the "GNU Affero General Public License v3.0 only", and the "Server Side
 * Public License v 1"; you may not use this file except in compliance with, at
 * your election, the "Elastic License 2.0", the "GNU Affero General Public
 * License v3.0 only", or the "Server Side Public License, v 1".
 */

/**
 * In case of changes in the grammar, this script should be updated: esql_update_ast_script.js
 */

import { IndexPatternContext, QualifiedNameContext, QualifiedNamePatternContext, InputParamContext, InputNamedOrPositionalParamContext, IdentifierOrParameterContext, InputNamedOrPositionalDoubleParamsContext, InputDoubleParamsContext } from '../antlr/esql_parser';
import { DOUBLE_TICKS_REGEX, SINGLE_BACKTICK, TICKS_REGEX } from './constants';
import { parseIdentifier, getPosition } from './helpers';
import { Builder } from '../builder';
export function nonNullable(v) {
  return v != null;
}
export function createAstBaseItem(name, ctx) {
  return {
    name,
    text: ctx.getText(),
    location: getPosition(ctx.start, ctx.stop),
    incomplete: Boolean(ctx.exception)
  };
}
const createParserFields = ctx => ({
  text: ctx.getText(),
  location: getPosition(ctx.start, ctx.stop),
  incomplete: Boolean(ctx.exception)
});
export const createCommand = (name, ctx, partial) => {
  const command = Builder.command({
    name,
    args: []
  }, createParserFields(ctx));
  if (partial) {
    Object.assign(command, partial);
  }
  return command;
};
export const createInlineCast = (ctx, value) => Builder.expression.inlineCast({
  castType: ctx.dataType().getText().toLowerCase(),
  value
}, createParserFields(ctx));
export const createList = (ctx, values) => Builder.expression.literal.list({
  values
}, createParserFields(ctx));
export const createNumericLiteral = (ctx, literalType) => Builder.expression.literal.numeric({
  value: Number(ctx.getText()),
  literalType
}, createParserFields(ctx));
export function createFakeMultiplyLiteral(ctx, literalType) {
  return {
    type: 'literal',
    literalType,
    text: ctx.getText(),
    name: ctx.getText(),
    value: ctx.PLUS() ? 1 : -1,
    location: getPosition(ctx.start, ctx.stop),
    incomplete: Boolean(ctx.exception)
  };
}
export function createLiteralString(ctx) {
  var _ctx$QUOTED_STRING$ge, _ctx$QUOTED_STRING;
  const quotedString = (_ctx$QUOTED_STRING$ge = (_ctx$QUOTED_STRING = ctx.QUOTED_STRING()) === null || _ctx$QUOTED_STRING === void 0 ? void 0 : _ctx$QUOTED_STRING.getText()) !== null && _ctx$QUOTED_STRING$ge !== void 0 ? _ctx$QUOTED_STRING$ge : '""';
  const isTripleQuoted = quotedString.startsWith('"""') && quotedString.endsWith('"""');
  let valueUnquoted = isTripleQuoted ? quotedString.slice(3, -3) : quotedString.slice(1, -1);
  if (!isTripleQuoted) {
    valueUnquoted = valueUnquoted.replace(/\\"/g, '"').replace(/\\r/g, '\r').replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\\\/g, '\\');
  }
  return Builder.expression.literal.string(valueUnquoted, {
    name: quotedString
  }, createParserFields(ctx));
}
function isMissingText(text) {
  return /<missing /.test(text);
}
export function textExistsAndIsValid(text) {
  return !!(text && !isMissingText(text));
}
export function createLiteral(type, node) {
  if (!node) {
    return {
      type: 'literal',
      name: 'unknown',
      text: 'unknown',
      value: 'unknown',
      literalType: type,
      location: {
        min: 0,
        max: 0
      },
      incomplete: false
    };
  }
  const text = node.getText();
  const partialLiteral = {
    type: 'literal',
    text,
    name: text,
    location: getPosition(node.symbol),
    incomplete: isMissingText(text)
  };
  if (type === 'double' || type === 'integer') {
    return {
      ...partialLiteral,
      literalType: type,
      value: Number(text),
      paramType: 'number'
    };
  } else if (type === 'param') {
    throw new Error('Should never happen');
  }
  return {
    ...partialLiteral,
    literalType: type,
    value: text
  };
}
export function createTimeUnit(ctx) {
  return {
    type: 'timeInterval',
    quantity: Number(ctx.integerValue().INTEGER_LITERAL().getText()),
    unit: ctx.UNQUOTED_IDENTIFIER().symbol.text,
    text: ctx.getText(),
    location: getPosition(ctx.start, ctx.stop),
    name: `${ctx.integerValue().INTEGER_LITERAL().getText()} ${ctx.UNQUOTED_IDENTIFIER().symbol.text}`,
    incomplete: Boolean(ctx.exception)
  };
}
export function createFunction(name, ctx, customPosition, subtype) {
  const node = {
    type: 'function',
    name,
    text: ctx.getText(),
    location: customPosition !== null && customPosition !== void 0 ? customPosition : getPosition(ctx.start, ctx.stop),
    args: [],
    incomplete: Boolean(ctx.exception)
  };
  if (subtype) {
    node.subtype = subtype;
  }
  return node;
}
export const createFunctionCall = ctx => {
  const functionExpressionCtx = ctx.functionExpression();
  const functionName = functionExpressionCtx.functionName();
  const node = {
    type: 'function',
    subtype: 'variadic-call',
    name: functionName.getText().toLowerCase(),
    text: ctx.getText(),
    location: getPosition(ctx.start, ctx.stop),
    args: [],
    incomplete: Boolean(ctx.exception)
  };
  const identifierOrParameter = functionName.identifierOrParameter();
  if (identifierOrParameter instanceof IdentifierOrParameterContext) {
    const operator = createIdentifierOrParam(identifierOrParameter);
    if (operator) {
      node.operator = operator;
    }
  }
  return node;
};
export const createBinaryExpression = (operator, ctx, args) => {
  const node = Builder.expression.func.binary(operator, args, {}, {
    text: ctx.getText(),
    location: getPosition(ctx.start, ctx.stop),
    incomplete: Boolean(ctx.exception)
  });
  return node;
};
export const createIdentifierOrParam = ctx => {
  var _ctx$parameter;
  const identifier = ctx.identifier();
  if (identifier) {
    return createIdentifier(identifier);
  }
  const parameter = (_ctx$parameter = ctx.parameter()) !== null && _ctx$parameter !== void 0 ? _ctx$parameter : ctx.doubleParameter();
  if (parameter) {
    return createParam(parameter);
  }
};
export const createIdentifier = identifier => {
  const text = identifier.getText();
  const name = parseIdentifier(text);
  return Builder.identifier({
    name
  }, createParserFields(identifier));
};
export const createParam = ctx => {
  if (ctx instanceof InputParamContext || ctx instanceof InputDoubleParamsContext) {
    const isDoubleParam = ctx instanceof InputDoubleParamsContext;
    const paramKind = isDoubleParam ? '??' : '?';
    return Builder.param.unnamed(createParserFields(ctx), {
      paramKind
    });
  } else if (ctx instanceof InputNamedOrPositionalParamContext || ctx instanceof InputNamedOrPositionalDoubleParamsContext) {
    const isDoubleParam = ctx instanceof InputNamedOrPositionalDoubleParamsContext;
    const paramKind = isDoubleParam ? '??' : '?';
    const text = ctx.getText();
    const value = text.slice(isDoubleParam ? 2 : 1);
    const valueAsNumber = Number(value);
    const isPositional = String(valueAsNumber) === value;
    const parserFields = createParserFields(ctx);
    if (isPositional) {
      return Builder.param.positional({
        paramKind,
        value: valueAsNumber
      }, parserFields);
    } else {
      return Builder.param.named({
        paramKind,
        value
      }, parserFields);
    }
  }
};
export const createOrderExpression = (ctx, arg, order, nulls) => {
  const node = Builder.expression.order(arg, {
    order,
    nulls
  }, createParserFields(ctx));
  return node;
};
function walkFunctionStructure(args, initialLocation, prop, getNextItemIndex) {
  let nextArg = args[getNextItemIndex(args)];
  const location = {
    ...initialLocation
  };
  while (Array.isArray(nextArg) || nextArg) {
    if (Array.isArray(nextArg)) {
      nextArg = nextArg[getNextItemIndex(nextArg)];
    } else {
      location[prop] = Math[prop](location[prop], nextArg.location[prop]);
      if (nextArg.type === 'function') {
        nextArg = nextArg.args[getNextItemIndex(nextArg.args)];
      } else {
        nextArg = undefined;
      }
    }
  }
  return location[prop];
}
export function computeLocationExtends(fn) {
  const location = fn.location;
  if (fn.args) {
    // get min location navigating in depth keeping the left/first arg
    location.min = walkFunctionStructure(fn.args, location, 'min', () => 0);
    // get max location navigating in depth keeping the right/last arg
    location.max = walkFunctionStructure(fn.args, location, 'max', args => args.length - 1);
    // in case of empty array as last arg, bump the max location by 3 chars (empty brackets)
    if (Array.isArray(fn.args[fn.args.length - 1]) && !fn.args[fn.args.length - 1].length) {
      location.max += 3;
    }
  }
  return location;
}

// Note: do not import esql_parser or bundle size will grow up by ~500 kb
/**
 * Do not touch this piece of code as it is auto-generated by a script
 */

/* SCRIPT_MARKER_START */
function getQuotedText(ctx) {
  return [27 /* esql_parser.QUOTED_STRING */, 69 /* esql_parser.QUOTED_IDENTIFIER */].map(keyCode => ctx.getToken(keyCode, 0)).filter(nonNullable)[0];
}
function getUnquotedText(ctx) {
  return [68 /* esql_parser.UNQUOTED_IDENTIFIER */, 77 /* esql_parser.UNQUOTED_SOURCE */].map(keyCode => ctx.getToken(keyCode, 0)).filter(nonNullable)[0];
}
/* SCRIPT_MARKER_END */

function isQuoted(text) {
  return text && /^(`)/.test(text);
}

/**
 * Follow a similar logic to the ES one:
 * * remove backticks at the beginning and at the end
 * * remove double backticks
 */
function safeBackticksRemoval(text) {
  return (text === null || text === void 0 ? void 0 : text.replace(TICKS_REGEX, '').replace(DOUBLE_TICKS_REGEX, SINGLE_BACKTICK)) || '';
}
function sanitizeSourceString(ctx) {
  const contextText = ctx.getText();
  // If wrapped by triple quote, remove
  if (contextText.startsWith(`"""`) && contextText.endsWith(`"""`)) {
    return contextText.replace(/\"\"\"/g, '');
  }
  // If wrapped by single quote, remove
  if (contextText.startsWith(`"`) && contextText.endsWith(`"`)) {
    return contextText.slice(1, -1);
  }
  return contextText;
}
const unquoteIndexString = indexString => {
  const isStringQuoted = indexString[0] === '"';
  if (!isStringQuoted) {
    return indexString;
  }

  // If wrapped by triple double quotes, simply remove them.
  if (indexString.startsWith(`"""`) && indexString.endsWith(`"""`)) {
    return indexString.slice(3, -3);
  }

  // If wrapped by double quote, remove them and unescape the string.
  if (indexString[indexString.length - 1] === '"') {
    indexString = indexString.slice(1, -1);
    indexString = indexString.replace(/\\"/g, '"').replace(/\\r/g, '\r').replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\\\/g, '\\');
    return indexString;
  }

  // This should never happen, but if it does, return the original string.
  return indexString;
};
export function sanitizeIdentifierString(ctx) {
  var _getUnquotedText, _getQuotedText;
  const result = ((_getUnquotedText = getUnquotedText(ctx)) === null || _getUnquotedText === void 0 ? void 0 : _getUnquotedText.getText()) || safeBackticksRemoval((_getQuotedText = getQuotedText(ctx)) === null || _getQuotedText === void 0 ? void 0 : _getQuotedText.getText()) || safeBackticksRemoval(ctx.getText()); // for some reason some quoted text is not detected correctly by the parser
  // TODO - understand why <missing null> is now returned as the match text for the FROM command
  return result === '<missing null>' ? '' : result;
}
export function wrapIdentifierAsArray(identifierCtx) {
  return Array.isArray(identifierCtx) ? identifierCtx : [identifierCtx];
}
export function createSetting(policyName, mode) {
  return {
    type: 'mode',
    name: mode.replace('_', '').toLowerCase(),
    text: mode,
    location: getPosition(policyName, {
      stop: policyName.start + mode.length - 1
    }),
    // unfortunately this is the only location we have
    incomplete: false
  };
}

/**
 * In https://github.com/elastic/elasticsearch/pull/103949 the ENRICH policy name
 * changed from rule to token type so we need to handle this specifically
 */
export function createPolicy(token, policy) {
  return {
    type: 'source',
    name: policy,
    text: policy,
    sourceType: 'policy',
    location: getPosition({
      start: token.stop - policy.length + 1,
      stop: token.stop
    }),
    // take into account ccq modes
    incomplete: false
  };
}
export function createSource(ctx, type = 'index') {
  const text = sanitizeSourceString(ctx);
  let cluster = '';
  let index = '';
  if (ctx instanceof IndexPatternContext) {
    const clusterString = ctx.clusterString();
    const indexString = ctx.indexString();
    if (clusterString) {
      cluster = clusterString.getText();
    }
    if (indexString) {
      index = indexString.getText();
      index = unquoteIndexString(index);
    }
  }
  return {
    type: 'source',
    cluster,
    index,
    name: text,
    sourceType: type,
    location: getPosition(ctx.start, ctx.stop),
    incomplete: Boolean(ctx.exception || text === ''),
    text: ctx === null || ctx === void 0 ? void 0 : ctx.getText()
  };
}
export function createColumnStar(ctx) {
  const text = ctx.getText();
  const parserFields = {
    text,
    location: getPosition(ctx.symbol),
    incomplete: ctx.getText() === '',
    quoted: false
  };
  const node = Builder.expression.column({
    args: [Builder.identifier({
      name: '*'
    }, parserFields)]
  }, parserFields);
  node.name = text;
  return node;
}
export function createColumn(ctx) {
  const args = [];
  if (ctx instanceof QualifiedNamePatternContext) {
    const list = ctx.identifierPattern_list();
    for (const identifierPattern of list) {
      const ID_PATTERN = identifierPattern.ID_PATTERN();
      if (ID_PATTERN) {
        const name = parseIdentifier(ID_PATTERN.getText());
        const node = Builder.identifier({
          name
        }, createParserFields(identifierPattern));
        args.push(node);
      } else {
        const parameter = createParam(identifierPattern.parameter());
        if (parameter) {
          args.push(parameter);
        }
      }
    }
  } else if (ctx instanceof QualifiedNameContext) {
    const list = ctx.identifierOrParameter_list();
    for (const item of list) {
      if (item instanceof IdentifierOrParameterContext) {
        const node = createIdentifierOrParam(item);
        if (node) {
          args.push(node);
        }
      }
    }
  } else {
    const name = sanitizeIdentifierString(ctx);
    const node = Builder.identifier({
      name
    }, createParserFields(ctx));
    args.push(node);
  }
  const text = sanitizeIdentifierString(ctx);
  const hasQuotes = Boolean(getQuotedText(ctx) || isQuoted(ctx.getText()));
  const column = Builder.expression.column({
    args
  }, {
    text: ctx.getText(),
    location: getPosition(ctx.start, ctx.stop),
    incomplete: Boolean(ctx.exception || text === '')
  });
  column.name = text;
  column.quoted = hasQuotes;
  return column;
}
export function createOption(name, ctx) {
  var _ctx$children;
  return {
    type: 'option',
    name,
    text: ctx.getText(),
    location: getPosition(ctx.start, ctx.stop),
    args: [],
    incomplete: Boolean(ctx.exception || ((_ctx$children = ctx.children) === null || _ctx$children === void 0 ? void 0 : _ctx$children.some(c => {
      // @ts-expect-error not exposed in type but exists see https://github.com/antlr/antlr4/blob/v4.11.1/runtime/JavaScript/src/antlr4/tree/ErrorNodeImpl.js#L19
      return Boolean(c.isErrorNode);
    })))
  };
}
export function createUnknownItem(ctx) {
  return {
    type: 'unknown',
    name: 'unknown',
    text: ctx.getText(),
    location: getPosition(ctx.start, ctx.stop),
    incomplete: Boolean(ctx.exception)
  };
}
export function createError(exception) {
  const token = exception.offendingToken;
  return {
    type: 'error',
    text: `SyntaxError: ${exception.message}`,
    location: getPosition(token)
  };
}